import { useLayoutEffect, useState, type RefObject } from "react";

const FEED = ".discovery-workspace--list .discovery-feed";

type ScrollSource = HTMLElement | Document;
type ScrollState = { last: number; anchor: number; direction: number };

function position(source: ScrollSource): number {
  const element = source === document ? document.scrollingElement ?? document.documentElement : source as HTMLElement;
  const max = Math.max(0, element.scrollHeight - element.clientHeight);
  return Math.min(max, Math.max(0, element.scrollTop));
}

function blocksHiding(header: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (header?.contains(active)) return true;
  if (active instanceof HTMLElement) {
    if (active.matches(":focus-visible")) return true;
    if (active.matches("input:not([readonly]):not([disabled]), textarea:not([readonly]):not([disabled]), select:not([disabled])")) return true;
    const editable = active.closest("[contenteditable]");
    if (editable && editable.getAttribute("contenteditable") !== "false") return true;
  }
  return !!header?.querySelector('[aria-expanded="true"], .header-app-search--expanded');
}

/** Returns whether the mobile shell header should be hidden. */
export function useScrollAwareHeader(enabled: boolean, screenKey: string, headerRef: RefObject<HTMLElement>): boolean {
  const [hidden, setHidden] = useState(false);

  useLayoutEffect(() => {
    setHidden(false);
    if (!enabled) return;

    const media = window.matchMedia?.("(max-width: 899px)");
    const isMobile = () => media?.matches ?? window.innerWidth <= 899;
    let states = new WeakMap<ScrollSource, ScrollState>();
    let lastSource: ScrollSource | null = null;
    let frame: number | null = null;
    let pending: ScrollSource[] = [];

    const seed = (source: ScrollSource) => {
      const value = position(source);
      states.set(source, { last: value, anchor: value, direction: 0 });
    };
    const reset = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      pending = [];
      states = new WeakMap();
      lastSource = null;
      seed(document);
      const main = document.querySelector<HTMLElement>(".main-content");
      const feed = document.querySelector<HTMLElement>(FEED);
      if (main) seed(main);
      if (feed) seed(feed);
      setHidden(false);
    };
    const process = () => {
      frame = null;
      const sources = pending;
      pending = [];
      for (const source of sources) {
        const feed = document.querySelector<HTMLElement>(FEED);
        if (!isMobile() || (screenKey === "discover" ? source !== feed : source !== document && source !== document.querySelector(".main-content"))) continue;
        const value = position(source);
        let state = states.get(source);
        if (!state) {
          state = { last: 0, anchor: 0, direction: 0 };
          states.set(source, state);
        } else if (source !== lastSource && lastSource !== null) {
          state.last = value;
          state.anchor = value;
          state.direction = 0;
        }
        lastSource = source;
        if (blocksHiding(headerRef.current) || value === 0) {
          state.last = value;
          state.anchor = value;
          state.direction = 0;
          setHidden(false);
          continue;
        }
        const direction = Math.sign(value - state.last);
        if (direction && direction !== state.direction) {
          state.anchor = state.last;
          state.direction = direction;
        }
        state.last = value;
        if (state.direction > 0 && value - state.anchor > 24) setHidden(true);
        if (state.direction < 0 && state.anchor - value > 12) setHidden(false);
      }
    };
    const onScroll = (event: Event) => {
      const target = event.target instanceof Window ? document : event.target;
      if (!(target instanceof HTMLElement) && target !== document) return;
      const feed = document.querySelector<HTMLElement>(FEED);
      if (screenKey === "discover" ? target !== feed : target !== document && target !== document.querySelector(".main-content")) return;
      if (!isMobile()) return;
      pending = pending.filter((source) => source !== target);
      pending.push(target as ScrollSource);
      if (frame === null) frame = requestAnimationFrame(process);
    };
    const revealIfBlocked = () => {
      if (blocksHiding(headerRef.current)) reset();
    };
    const observer = new MutationObserver(revealIfBlocked);
    if (headerRef.current) observer.observe(headerRef.current, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-expanded", "class"] });
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("focusin", revealIfBlocked);
    media?.addEventListener("change", reset);
    window.addEventListener("resize", reset);
    reset();
    return () => {
      observer.disconnect();
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("focusin", revealIfBlocked);
      media?.removeEventListener("change", reset);
      window.removeEventListener("resize", reset);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [enabled, screenKey, headerRef]);

  return enabled && hidden;
}
